import { useRouter } from 'next/router';
import React, { FC, useCallback, useMemo, useState } from 'react';

import DynamicLoader from '@/atoms/dynamic-loader';
import { Location, Unit } from '@core/booking/booking.types';

import LandingCalendarView from './LandingCalendarView';
import LocationsMap from './LocationsMap';
import MeetingRoomLandingHeader from './MeetingRoomLandingHeader';
import RoomCardGrid from './RoomCardGrid';
import { LandingViewMode } from './meeting-room-landing.types';
import { useMeetingRoomLanding } from './useMeetingRoomLanding';

const MeetingRoomLanding: FC = () => {
  const { loading, cityGroups } = useMeetingRoomLanding();
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<LandingViewMode>('grid');
  const router = useRouter();
  const { clientName } = router.query;

  const handleCityFilter = useCallback((city: string | null) => {
    setSelectedCity(city);
  }, []);

  const handleViewChange = useCallback((mode: LandingViewMode) => {
    setViewMode(mode);
  }, []);

  const filteredCityGroups = useMemo(() => {
    if (!selectedCity) return cityGroups;
    return cityGroups.filter((group) => group.city === selectedCity);
  }, [cityGroups, selectedCity]);

  const handleRoomSelect = useCallback(
    (room: Unit, location: Location) => {
      router.push(`/booking/meeting-room/${room.id}?clientName=${clientName}&locationId=${location.id}`);
    },
    [router, clientName],
  );

  return (
    <div className="w-full overflow-y-auto no-scrollbar">
      <MeetingRoomLandingHeader
        cityGroups={cityGroups}
        selectedCity={selectedCity}
        onCityFilter={handleCityFilter}
        viewMode={viewMode}
        onViewChange={handleViewChange}
      />

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <DynamicLoader size="large" />
          <p className="mt-4 text-lg" style={{ color: '#707070' }}>
            Loading available rooms...
          </p>
        </div>
      ) : viewMode === 'calendar' ? (
        <div className="px-4 md:px-16">
          <LandingCalendarView cityGroups={filteredCityGroups} clientName={clientName as string} />
        </div>
      ) : (
        <div className="px-4 md:px-16">
          {/* Mobile: map above cards when visible */}
          {viewMode === 'map' && (
            <div className="block lg:hidden mb-6" style={{ height: 300 }}>
              <LocationsMap cityGroups={filteredCityGroups} height="300px" onRoomSelect={handleRoomSelect} />
            </div>
          )}

          {/* Desktop: side-by-side layout */}
          <div className="flex gap-6">
            {/* Cards - scrollable */}
            <div className={viewMode === 'map' ? 'flex-1 min-w-0' : 'w-full'}>
              <RoomCardGrid cityGroups={filteredCityGroups} onRoomSelect={handleRoomSelect} />
            </div>

            {/* Map - sticky on desktop */}
            {viewMode === 'map' && (
              <div
                className="hidden lg:block flex-shrink-0 sticky top-0 self-start"
                style={{ width: '40%', height: 'calc(100vh - 120px)' }}
              >
                <LocationsMap cityGroups={filteredCityGroups} height="100%" onRoomSelect={handleRoomSelect} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingRoomLanding;
